import { createAppRequest } from '@/services/request';
import type {
  CreateReceivableRequest,
  GenerateBillingRequest,
  GenerateBillingResult,
  ParkPageResult,
  ParkReceipt,
  ParkReceivable,
  CollectionLedgerRow,
  FinanceOverviewSummary,
  ParkExpenseApplication,
  ParkInvoiceApplication,
  ExpenseSummary,
  ParkSettlement,
  ReceiptListQuery,
  ReceivableListQuery,
  CollectReceivableRequest,
  CollectReceivableResult,
  InvoiceableOrder,
  PrepaidBalance,
  RegisterReceiptRequest,
  SettleRequest,
} from './types/finance';

const request = createAppRequest();

interface ParkApiEnvelope<T> {
  code: number;
  msg: string;
  data: T;
}

const requestFinance = async <T>(url: string, options?: Parameters<typeof request>[1]) => {
  const response = await request<ParkApiEnvelope<T>>(url, options);
  return response.data;
};

const BASE = '/bff/operator/finance';

/**
 * 园区财务接口。
 *
 * park 上下文由请求头 x-park-uuid 提供（见 park-context.util），
 * 因此这里不传 parkUuid，与现有 parkApi 的其它模块保持一致。
 */
export const financeApi = {
  /** 财务总览：后端聚合。 */
  overview: () => requestFinance<FinanceOverviewSummary>(`${BASE}/overview`, { method: 'GET' }),

  // ---------- 应收 ----------

  listReceivables: (params?: ReceivableListQuery) =>
    requestFinance<ParkPageResult<ParkReceivable>>(`${BASE}/receivables`, {
      method: 'GET',
      params,
    }),

  getReceivable: (uuid: string) =>
    requestFinance<ParkReceivable>(`${BASE}/receivables/${uuid}`, { method: 'GET' }),

  createReceivable: (data: CreateReceivableRequest) =>
    requestFinance<{ receivable: ParkReceivable; created: boolean }>(
      `${BASE}/receivables`,
      { method: 'POST', data },
    ),

  /** 批量出账：租赁 + 物业。 */
  generateBilling: (data: GenerateBillingRequest) =>
    requestFinance<GenerateBillingResult>(`${BASE}/receivables/generate`, {
      method: 'POST',
      data,
    }),

  /** 催缴台账：租赁、物业、水电、车位统一催收。 */
  collectionLedger: (params?: {
    feeType?: string;
    enterpriseUuid?: string;
    overdueOnly?: boolean;
    pageNumber?: number;
    pageSize?: number;
  }) =>
    requestFinance<ParkPageResult<CollectionLedgerRow>>(`${BASE}/collection-ledger`, {
      method: 'GET',
      params,
    }),

  // ---------- 发票管理 ----------

  listInvoices: (params?: {
    status?: string;
    orderUuid?: string;
    keyword?: string;
    pageNumber?: number;
    pageSize?: number;
  }) =>
    requestFinance<ParkPageResult<ParkInvoiceApplication>>(`${BASE}/invoices`, {
      method: 'GET',
      params,
    }),

  /** 可开票余额：只算已结清应收。 */
  /** 可开票订单：财务发起申请时先选订单。 */
  listInvoiceableOrders: (keyword?: string) =>
    requestFinance<InvoiceableOrder[]>(`${BASE}/invoices/invoiceable-orders`, {
      method: 'GET',
      params: { keyword },
    }),

  invoiceable: (orderUuid: string) =>
    requestFinance<{ amount: number }>(`${BASE}/invoices/invoiceable`, {
      method: 'GET',
      params: { orderUuid },
    }),

  createInvoice: (data: {
    orderUuid?: string | null;
    enterpriseUuid?: string | null;
    enterpriseName: string;
    receivableUuids?: string[] | null;
    amount: number;
    invoiceType: string;
    taxRate?: number | null;
    title: string;
    taxNo?: string | null;
    remark?: string | null;
  }) => requestFinance<ParkInvoiceApplication>(`${BASE}/invoices`, { method: 'POST', data }),

  issueInvoice: (uuid: string, data: { invoiceNo: string; amount: number; issuedAt?: string }) =>
    requestFinance<ParkInvoiceApplication>(`${BASE}/invoices/${uuid}/issue`, {
      method: 'POST',
      data,
    }),

  redInvoice: (uuid: string, reason: string) =>
    requestFinance<{ redAmount: number }>(`${BASE}/invoices/${uuid}/red`, {
      method: 'POST',
      data: { reason },
    }),

  // ---------- 费用申请 / 应付 ----------

  listExpenses: (params?: {
    status?: string;
    type?: string;
    contractUuid?: string;
    keyword?: string;
    pageNumber?: number;
    pageSize?: number;
  }) =>
    requestFinance<ParkPageResult<ParkExpenseApplication>>(`${BASE}/expenses`, {
      method: 'GET',
      params,
    }),

  expenseSummary: () =>
    requestFinance<ExpenseSummary>(`${BASE}/expenses/summary`, { method: 'GET' }),

  createExpense: (data: {
    type: string;
    contractUuid?: string | null;
    supplierUuid?: string | null;
    payeeName: string;
    amount: number;
    expenseCategory?: string | null;
    purpose: string;
    remark?: string | null;
  }) => requestFinance<ParkExpenseApplication>(`${BASE}/expenses`, { method: 'POST', data }),

  submitExpense: (uuid: string) =>
    requestFinance<ParkExpenseApplication>(`${BASE}/expenses/${uuid}/submit`, { method: 'POST' }),

  approveExpense: (uuid: string) =>
    requestFinance<ParkExpenseApplication>(`${BASE}/expenses/${uuid}/approve`, { method: 'POST' }),

  rejectExpense: (uuid: string, reason: string) =>
    requestFinance<ParkExpenseApplication>(`${BASE}/expenses/${uuid}/reject`, {
      method: 'POST',
      data: { reason },
    }),

  payExpense: (uuid: string, paymentReference: string, voucherFileUuid?: string | null) =>
    requestFinance<ParkExpenseApplication>(`${BASE}/expenses/${uuid}/pay`, {
      method: 'POST',
      data: { paymentReference, voucherFileUuid },
    }),

  // ---------- 收款 ----------

  listReceipts: (params?: ReceiptListQuery) =>
    requestFinance<ParkPageResult<ParkReceipt>>(`${BASE}/receipts`, {
      method: 'GET',
      params,
    }),

  getReceipt: (uuid: string) =>
    requestFinance<ParkReceipt>(`${BASE}/receipts/${uuid}`, { method: 'GET' }),

  /** 预收余额汇总：财务对账要看哪些企业还有没核销的钱。 */
  listPrepaidBalances: () =>
    requestFinance<PrepaidBalance[]>(`${BASE}/receipts/prepaid-balances`, {
      method: 'GET',
    }),

  registerReceipt: (data: RegisterReceiptRequest) =>
    requestFinance<ParkReceipt>(`${BASE}/receipts`, { method: 'POST', data }),

  /**
   * 从应收单直接收款：一次提交完成「建收款单 + 核销」。
   * 一笔到账对多张应收的复杂场景仍走 registerReceipt + settle。
   */
  collectReceivable: (receivableUuid: string, data: CollectReceivableRequest) =>
    requestFinance<CollectReceivableResult>(
      `${BASE}/receivables/${receivableUuid}/collect`,
      { method: 'POST', data },
    ),

  listSettlements: (receiptUuid: string) =>
    requestFinance<ParkSettlement[]>(`${BASE}/receipts/${receiptUuid}/settlements`, {
      method: 'GET',
    }),

  // ---------- 核销 ----------

  settle: (data: SettleRequest) =>
    requestFinance<{ receiptUuid: string; settledAmount: number; allocations: number }>(
      `${BASE}/settlements`,
      { method: 'POST', data },
    ),

  reverseSettlement: (uuid: string, reason: string) =>
    requestFinance<{ reversedAmount: number }>(`${BASE}/settlements/${uuid}/reverse`, {
      method: 'POST',
      data: { reason },
    }),
};
